import { useMutation, useQueryClient } from '@tanstack/react-query'
import { onSuccessInvalidate } from '@/hooks/invalidation'
import { useDefinedQuery } from '@/hooks/useDefinedQuery'
import type { QueryDef } from '@/hooks/useDefinedQuery'
import { withIdempotency } from '@/hooks/withIdempotency'
import { api } from '@/services/api'
import { getUserFilters } from '@/services/api/filters'

const filtersQueries = {
  all: {
    queryKey: () => ['userFilters'] as const,
    queryFn: getUserFilters,
    staleTime: 5 * 60 * 1000,
    auth: 'required',
  } satisfies QueryDef<Awaited<ReturnType<typeof getUserFilters>>>,
}

export function useUserFilters() {
  return useDefinedQuery(filtersQueries.all)
}

// Wraps a single-arg filter mutation. Accepts either the bare arg (legacy)
// or an object `{ arg, idempotencyKey }`, always injecting an idempotency key.
function useFilterMutation<TArg>(mutationFn: (arg: TArg, idempotencyKey?: string) => Promise<unknown>) {
  const queryClient = useQueryClient()
  type Vars = { arg: TArg; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ arg, idempotencyKey }: Vars) => mutationFn(arg, idempotencyKey),
    ...onSuccessInvalidate(queryClient, 'userFilters'),
  })
  type PublicVars = TArg | Vars
  const isWrapped = (vars: PublicVars): vars is Vars =>
    typeof vars === 'object' && vars !== null && 'arg' in (vars as object)
  const normalize = (vars: PublicVars): Vars => (isWrapped(vars) ? vars : { arg: vars as TArg })
  return withIdempotency(mutation, normalize)
}

export function useHideSet() {
  return useFilterMutation<string>(api.hideSet)
}

export function useUnhideSet() {
  return useFilterMutation<string>(api.unhideSet)
}

export function useHideCard() {
  return useFilterMutation<number>(api.hideCard)
}

export function useUnhideCard() {
  return useFilterMutation<number>(api.unhideCard)
}

export function useHideRarity() {
  return useFilterMutation<{ setCode: string; rarity: string }>(({ setCode, rarity }, idempotencyKey) =>
    api.hideRarity(setCode, rarity, idempotencyKey),
  )
}

export function useUnhideRarity() {
  return useFilterMutation<{ setCode: string; rarity: string }>(({ setCode, rarity }, idempotencyKey) =>
    api.unhideRarity(setCode, rarity, idempotencyKey),
  )
}

export function useHideSealedProduct() {
  return useFilterMutation<number>(api.hideSealedProduct)
}

export function useUnhideSealedProduct() {
  return useFilterMutation<number>(api.unhideSealedProduct)
}
